import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'

/**
 * Date layouts the games/scores/events importers can be told to expect. Values
 * are the PHP formats the API validates against; labels show each layout with
 * the same date (4 March 2026) so day and month are unmistakable.
 */
export const DATE_FORMATS = [
  { value: 'Y-m-d', label: 'YYYY-MM-DD - Default (E.g. 2026-03-04)' },
  { value: 'd/m/Y', label: 'DD/MM/YYYY (E.g. 04/03/2026)' },
  { value: 'm/d/Y', label: 'MM/DD/YYYY (E.g. 03/04/2026)' },
  { value: 'd-m-Y', label: 'DD-MM-YYYY (E.g. 04-03-2026)' },
  { value: 'm-d-Y', label: 'MM-DD-YYYY (E.g. 03-04-2026)' },
  { value: 'd.m.Y', label: 'DD.MM.YYYY (E.g. 04.03.2026)' },
  { value: 'Y/m/d', label: 'YYYY/MM/DD (E.g. 2026/03/04)' },
]

/**
 * The format the exports write and the importer expects unless told otherwise.
 */
export const DEFAULT_DATE_FORMAT = 'Y-m-d'

export const getConfig = () => ({
  multiTeamRoster: {
    linkText: 'Team Roster Import Template',
    fileName: 'sportninja_multi_team_roster_template.csv',
    blurb:
      "Click here to download the Multi-team Team Roster Import Template, which can be used to upload your team's player roster",
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: 'Import Player Rosters',
    type: 'multiTeamRoster',
  },
  roster: {
    linkText: 'Team Roster Import Template',
    fileName: 'sportninja_roster_template.csv',
    blurb:
      "Click here to download the Team Roster Import Template, which can be used to upload your team's player roster",
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importPlayerRoster'),
    type: 'roster',
  },
  multiTeamOfficials: {
    linkText: 'Team Officials Import Template',
    fileName: 'sportninja_multi_team_officials_template.csv',
    blurb:
      "Click here to download the Multi-team Team Officials Import Template, which can be used to upload your teams' managers, coaches and trainers",
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: 'Import Team Official Rosters',
    type: 'multiTeamOfficials',
  },
  teamOfficials: {
    linkText: 'Team Officials Import Template',
    fileName: 'sportninja_roster_team_officials_template.csv',
    blurb:
      "Click here to download the Team Officials Import Template, which can be used to upload your team's managers, coaches and trainers",
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importTeamOfficialsRoster'),
    type: 'teamOfficials',
  },
  schedule: {
    linkText: `${t('common:competition')} Import Template`,
    fileName: 'sportninja_schedule_template.csv',
    blurb: `Click here to download the ${t('util:capitalize', {
      text: t('common:competition'),
    })} Import Template, which can be used to upload your ${t(
      'common:competition'
    )} games`,
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importGames'),
    title: t('common:importGames'),
    dateFormats: true,
    type: 'schedule',
  },
  team: {
    linkText: 'Team Import Template',
    fileName: 'sportninja_team_template.csv',
    blurb:
      'Click here to download the Team Import Template, which can be used to upload your teams',
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importTeams'),
    type: 'team',
  },
  gameSlots: {
    linkText: 'Game Slots Import Template',
    fileName: 'sportninja_gameslots_template.csv',
    blurb:
      'Click here to download the Game Slots Import Template, which can be used to upload your game slots',
    SNHelpLink: 'https://support.sportninja.com/',
    label: 'Import Game Slots',
    type: 'gameSlots',
  },
  officialAssignments: {
    linkText: 'Officials Assignments Import Template',
    fileName: 'sportninja_officials_game_assignments_template.csv',
    blurb:
      'Click here to download the Officials Assignments Import Template. Export the assignments first, fill in the officials for each game, then upload it here',
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importOfficialAssignments'),
    successNoun: 'game assignments',
    type: 'officialAssignments',
  },
  scores: {
    linkText: 'Score Import Template',
    fileName: 'sportninja_score_template.csv',
    blurb:
      'Click here to download the Score Import Template. Export the scores first, enter each result and set Action to Score, then upload it here',
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importScores'),
    successNoun: 'game results',
    dateFormats: true,
    type: 'scores',
  },
  events: {
    linkText: 'Events Import Template',
    fileName: 'sportninja_events_template.csv',
    blurb:
      'Click here to download the Events Import Template, which can be used to upload practices, photo days and other competition events',
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: t('common:importEvents'),
    successNoun: 'events',
    dateFormats: true,
    type: 'events',
  },
  officials: {
    linkText: 'Official Import Template',
    fileName: 'sportninja_official_template.csv',
    blurb:
      'Click here to download the Official Import Template, which can be used to upload your officials',
    SNHelpLink: 'https://support.sportninja.com/import/export-basics',
    label: 'Import Officials',
    type: 'officials',
  },
})

const getImportConfig = (teamRosterId, importType, entityType) => {
  const config = getConfig()
  if (teamRosterId) {
    return config.roster
  }
  if (importType === 'teams') {
    return config.team
  }
  if (importType === ENTITY_TYPES.officials) {
    return config.officials
  }
  if (importType === 'officials/assignments') {
    return config.officialAssignments
  }
  if (importType === 'scores') {
    return config.scores
  }
  if (importType === 'events') {
    return config.events
  }
  if (entityType === ENTITY_TYPES.schedule) {
    return config.schedule
  }
  if (entityType === ENTITY_TYPES.gameSlots) {
    return config.gameSlots
  }
  return null
}

export default getImportConfig
